/** Une photo de la galerie produit. */
export interface ProductImage {
  url: string;
  alt: string;
}

/**
 * Caractéristiques et avis d'un produit, lus dans les metafields Shopify.
 *
 * Les listes sont toujours présentes, vides au pire : une étiquette manquante
 * n'est pas une anomalie. Les autres champs restent absents quand le metafield
 * n'est pas renseigné, pour que l'affichage puisse simplement ne rien montrer.
 */
export interface ProductDetails {
  /** `custom.taille` : gabarits de chien visés. */
  tailles: string[];
  /** `custom.duree_de_mastication` : Courte, Moyenne, Longue, Indestructible. */
  dureeMastication: string[];
  /** `conseil.hardness`, de 1 à 5. */
  durete?: number;
  /** `conseil.duration`, de 1 à 5. */
  duree?: number;
  /** `conseil.digestion`, de 1 à 5. */
  digestion?: number;
  /** `conseil.fat_level`, de 1 à 5. */
  matiereGrasse?: number;
  /** `conseil.min_age_months`. */
  ageMinimumMois?: number;
  /** `conseil.min_weight_kg`. */
  poidsMinimumKg?: number;
  /** `conseil.suitable_for_gulpers` : convient aux chiens qui avalent tout rond. */
  pourGloutons?: boolean;
  /** `conseil.chew_family` : bois, corne, lamelle… */
  famille?: string;
  /** `conseil.proteins`. */
  proteines: string[];
  /** `reviews.rating`, la valeur seule, sur une échelle de 1 à 5. */
  note?: number;
  /** `reviews.rating_count`. */
  nombreAvis?: number;
  /** `custom.description_produit_courte`, converti en texte brut. */
  descriptionCourte?: string;
  /** `custom.notre_garantie` : la promesse de reprise, rédigée par la boutique. */
  garantie?: string;
  /** `custom.que_contient_ce_pack_` : le détail du contenu, ligne par ligne. */
  contenu?: string;
  /** `custom.pour_quel_type_de_chien`. */
  pourQuelChien?: string;
  /** `custom.conseils_d_utilisation_pack`. */
  conseils?: string;
}

/**
 * Une déclinaison achetable : le « Votre choix » de la fiche produit. La
 * boutique en vend de deux sortes, des quantités (1, 3 ou 10 oreilles) et des
 * formats de pack. Un produit sans déclinaison réelle n'en expose qu'une, dont
 * le titre vaut `Default Title` côté Shopify et n'est jamais affiché.
 */
export interface ProductVariant {
  id: string;
  /** Libellé de la déclinaison, déjà nettoyé de `Default Title`. */
  title: string;
  price: { amount: string; currencyCode: string };
  /** Prix barré, seulement si Shopify en déclare un et qu'il est supérieur. */
  compareAtPrice?: { amount: string; currencyCode: string };
  availableForSale: boolean;
}

/**
 * Offre d'abonnement rattachée au produit, telle que Shopify la publie. La
 * boutique n'en propose qu'une par produit, du type « Abonnez-vous et
 * économisez 10 % ».
 */
export interface SellingPlan {
  id: string;
  /** Nom du groupe, affiché tel quel : c'est lui qui porte la promesse. */
  name: string;
  /** Nom du plan choisi, qui porte la cadence de livraison. */
  cadence: string;
}

export interface ShopProduct {
  id: string;
  handle: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  /** Galerie de la fiche complète, image principale en tête. */
  images: ProductImage[];
  price: {
    amount: string;
    currencyCode: string;
  };
  variantId: string;
  availableForSale: boolean;
  productUrl?: string;
  /** Absent tant qu'aucun metafield n'a été renseigné sur le produit. */
  details?: ProductDetails;
  /**
   * Déclinaisons achetables, dans l'ordre du catalogue. Toujours au moins une.
   * Absent sur les données anciennes : la fiche retombe alors sur `variantId`.
   */
  variants?: ProductVariant[];
  /** Offre d'abonnement, quand le produit en a une. */
  sellingPlan?: SellingPlan;
  /**
   * Arguments de vente, extraits de la description riche du produit. Ce sont
   * les puces de la fiche Shopify, émoji compris.
   */
  highlights?: string[];
}

/** Tranche de collection : les produits et de quoi demander la suite. */
export interface ProductPage {
  products: ShopProduct[];
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface ProductRepository {
  getFeaturedProducts(): Promise<ShopProduct[]>;
  getProductsByHandles(handles: string[]): Promise<ShopProduct[]>;
  getProductByHandle(handle: string): Promise<ShopProduct | null>;
  /** `after` vaut le `endCursor` de la page précédente, absent pour la première. */
  getCollectionProducts(handle: string, first: number, after?: string | null): Promise<ProductPage>;
}

/**
 * Ce qu'il faut pour afficher une ligne sans réinterroger le catalogue.
 * Le panier simulé le conserve localement, le panier Storefront le relit
 * dans la réponse `cart`.
 */
export interface CartLineInfo {
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  imageUrl: string;
  imageAlt: string;
  /**
   * Plan d'abonnement choisi sur la fiche produit. Transmis tel quel à Shopify,
   * qui facture alors la ligne en récurrent plutôt qu'à l'unité. Absent pour un
   * achat ponctuel, c'est-à-dire l'immense majorité des lignes.
   */
  sellingPlanId?: string;
}

export interface CartLine extends CartLineInfo {
  variantId: string;
  quantity: number;
}

export interface CartService {
  createCart(): Promise<string>;
  /** `info` évite au panier simulé de deviner le titre ou le prix. */
  addLine(variantId: string, quantity: number, info?: CartLineInfo): Promise<void>;
  /** Une quantité inférieure à 1 retire la ligne. */
  updateLine(variantId: string, quantity: number): Promise<void>;
  removeLine(variantId: string): Promise<void>;
  getLines(): Promise<CartLine[]>;
  getCheckoutUrl(): Promise<string>;
  /** Nombre d'articles, disponible sans appel réseau pour le compteur de la barre. */
  lineCount(): number;
}

/** Erreur commerce affichable telle quelle au visiteur. */
export class CommerceError extends Error {
  constructor(
    message: string,
    readonly recoverable: boolean = true,
  ) {
    super(message);
    this.name = 'CommerceError';
  }
}
